import { ID } from '@griply/types';

import { BatchDocuments, WriteBatchFn } from '../batch-documents';
import { getTasksQuery } from '../queries/get-tasks-query';
import {
  FirestoreInstance,
  OrderByDirection,
  WhereFilterOp,
} from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type DeleteGoalsOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TQueryRef = unknown,
  TConstraint = unknown,
  TDb = FirestoreInstance,
> = {
  goalIds: ID[];
  uid: ID;
  db: TDb;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (collectionRef: NoInfer<TCollectionRef>, id: ID) => TDocRef;
  deleteDoc: (ref: NoInfer<TDocRef>) => Promise<void> | void;
  query: (
    ref: NoInfer<TCollectionRef>,
    ...constraints: TConstraint[]
  ) => TQueryRef;
  where: (field: string, op: WhereFilterOp, value: unknown) => TConstraint;
  orderBy: (field: string, direction?: OrderByDirection) => TConstraint;
  getDocs: (query: NoInfer<TQueryRef>) => Promise<{
    docs: ReadonlyArray<{ ref: TDocRef }>;
  }>;
  writeBatch?: WriteBatchFn<NoInfer<TDocRef>, NoInfer<TDb>>;
};

export const deleteGoals = async <
  TDocRef,
  TCollectionRef,
  TQueryRef,
  TConstraint,
  TDb = FirestoreInstance,
>(
  options: DeleteGoalsOptions<
    TDocRef,
    TCollectionRef,
    TQueryRef,
    TConstraint,
    TDb
  >,
): Promise<void> => {
  const {
    goalIds,
    uid,
    db,
    collection,
    doc,
    deleteDoc,
    query,
    where,
    orderBy,
    getDocs,
    writeBatch,
  } = options;

  if (!goalIds.length) {
    return;
  }

  const goalCollection = collection(db, FirestoreCollections.Goals);
  const taskRefs: TDocRef[] = [];

  for (const goalId of goalIds) {
    const tasksQuery = getTasksQuery<
      TCollectionRef,
      TQueryRef,
      TConstraint,
      TDb
    >({
      db,
      collection,
      query,
      where,
      orderBy,
      uid,
      goals: [goalId],
      showCompleted: true,
    });
    const snapshot = await getDocs(tasksQuery);
    snapshot.docs.forEach((task) => taskRefs.push(task.ref));
  }

  const goalRefs = goalIds.map((id) => doc(goalCollection, id));

  if (!writeBatch) {
    await Promise.all([
      ...taskRefs.map((ref) => deleteDoc(ref)),
      ...goalRefs.map((ref) => deleteDoc(ref)),
    ]);
    return;
  }

  const batch = new BatchDocuments(db, writeBatch);
  taskRefs.forEach((ref) => batch.deleteDocument(ref));
  goalRefs.forEach((ref) => batch.deleteDocument(ref));
  await batch.commit();
};
